import './styles/order-status.scss';

import {
    Breadcrumb,
    Button,
    Input,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import React, { useState } from 'react';
import {
    validateEmail,
    validateIsNumber,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/validations';

import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { PageTitle } from '../components/Primitives';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { graphql } from 'gatsby';
import rightArrowIcon from '../images/englander-icons/Right-Arrow-Icon.svg';
import { useCallback } from 'react';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useStore } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query {
        metadata: allProductsJson(
            filter: { code: { eq: "englander_status-commande" } }
        ) {
            nodes {
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                description {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    content {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    texte_1 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

type OrderData =
    | {
          order:
              | {
                    increment_id: string;
                    status: string;
                    items: {
                        name: string;
                        sku: string;
                        price: string;
                        qty_ordered: string;
                        row_total: string;
                    }[];
                    store_currency_code: string;
                    subtotal: number;
                    discount_amount: number;
                    shipping_amount: number;
                    base_tax_amount: number;
                    total_invoiced: number;
                    billing_address: {
                        firstname: string;
                        lastname: string;
                        street: string[];
                        city: string;
                        region: string;
                        postcode: string;
                        telephone: string;
                    };
                    shipping_address: {
                        firstname: string;
                        lastname: string;
                        street: string[];
                        city: string;
                        region: string;
                        postcode: string;
                        telephone: string;
                    };
                }
              | undefined;
          shipping:
              | {
                    title: string;
                    track_number: string;
                }[]
              | undefined;
      }
    | undefined;

export default function OrderStatus({ data }) {
    const { currentLocale } = usePageLocalization();
    const metadata = data?.metadata?.nodes?.[0];
    const metadataAttributes = metadata?.attributes;
    const pageTitle = metadata?.title?.[currentLocale];
    const pageDesc = metadata?.description?.[currentLocale];
    const headerText =
        metadataAttributes?.texte_1?.values?.[0]?.[currentLocale];
    const content = metadataAttributes?.content?.values?.[0]?.[currentLocale];
    const { t } = useTranslation('layout');
    const [email, setEmail] = useState('');
    const [orderId, setOrderId] = useState('');
    const [orderData, setOrderData] = useState<OrderData>(undefined);
    const [errors, setErrors] = useState({
        orderId: '',
        email: '',
        notfound: '',
    });
    const { currentStore } = useStore();
    const [loading, setLoading] = useState(false);

    const fetchOrderStatus = useCallback(() => {
        const requestOptions = {
            method: 'GET',
        };

        fetch(
            `/inquiry/order-status?email=${email}&order_id=${orderId}&store=${currentStore}`,
            requestOptions
        )
            .then((res) => {
                if (res.status !== 200) {
                    setOrderData(undefined);
                    setErrors({
                        ...errors,
                        notfound: t('error.order-not-found'),
                    });
                } else return res.json();
            })
            .then((data) => {
                setOrderData(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                setOrderData(undefined);
                setErrors({
                    ...errors,
                    notfound: t('error.order-not-found'),
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocale, email, errors, orderId, currentStore]);

    const locale =
        orderData?.order?.store_currency_code === 'USD' ? 'en_US' : 'fr_CA';

    return (
        <Layout>
            <SEO
                title={metadataAttributes?.meta_title.values[0][currentLocale]}
                description={
                    metadataAttributes?.meta_description.values[0][
                        currentLocale
                    ]
                }
            />
            <div className='container order-status'>
                <Breadcrumb
                    elements={[
                        { link: '/', name: t('layout:appliance.home') },
                        { link: '', name: pageTitle },
                    ]}
                />
                <PageTitle
                    title={pageTitle}
                    description={orderData === undefined ? pageDesc : undefined}
                />

                {orderData !== undefined ? (
                    <div className='order-status-data'>
                        <h3>
                            {t('order-status.order')} #
                            {orderData?.order?.increment_id}
                        </h3>
                        <p className='status'>
                            {t('order-status.status')}:{' '}
                            {!t(
                                `order-status.${orderData?.order?.status}`
                            ).includes('order-status.')
                                ? t(`order-status.${orderData?.order?.status}`)
                                : orderData?.order?.status}
                        </p>

                        {orderData?.order?.status !== 'processing' &&
                            orderData?.order?.status !== 'new' && (
                                <>
                                    <h3>{t('order-status.shipping-title')}</h3>
                                    <p>
                                        {t('order-status.carrier')}:{' '}
                                        {orderData?.shipping?.[0]?.title}
                                    </p>
                                    <p>
                                        {t('order-status.tracking')}:{' '}
                                        {orderData?.shipping?.[0]?.track_number}
                                    </p>
                                    <p className='tracking-msg'>
                                        {t('order-status.tracking-msg')}
                                    </p>
                                    <br />
                                    <br />
                                </>
                            )}

                        <h3>{t('order-status.ordered-items')}</h3>

                        <div className='ordered-items'>
                            <div className='tr'>
                                <div className='th'>
                                    {t('order-status.product-name')}
                                </div>
                                <div className='th'>SKU</div>
                                <div className='th'>
                                    {t('order-status.price')}
                                </div>
                                <div className='th'>
                                    {t('order-status.qty')}
                                </div>
                                <div className='th'>
                                    {t('order-status.subtotal')}
                                </div>
                            </div>

                            {orderData?.order?.items?.map((item) => {
                                return (
                                    <div
                                        className='tr'
                                        key={item.name + 'order-data'}
                                    >
                                        <div className='td'>{item.name}</div>
                                        <div className='td'>{item.sku}</div>
                                        <div className='td'>
                                            {LocalePrice(locale, item.price)}
                                        </div>
                                        <div className='td'>
                                            {item.qty_ordered}
                                        </div>
                                        <div className='td'>
                                            {LocalePrice(
                                                locale,
                                                item.row_total
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            <div className='order-info'>
                                <div className='labels'>
                                    <div className='label'>
                                        {t('order-status.subtotal')}
                                    </div>
                                    <div className='label'>
                                        {t('order-status.shipping')}
                                    </div>
                                    <div className='label'>
                                        {t('order-status.discount')}
                                    </div>
                                    <div className='label'>
                                        {t('order-status.tax')}
                                    </div>
                                    {/*   <div className='label'>
                                        Surcharge sur la livraison
                                    </div> */}
                                    <div className='label'>
                                        {t('order-status.grand-total')}
                                    </div>
                                </div>
                                <div className='prices'>
                                    <div className='price'>
                                        {LocalePrice(
                                            locale,
                                            orderData?.order?.subtotal
                                        )}
                                    </div>
                                    <div className='price'>
                                        {LocalePrice(
                                            locale,
                                            orderData?.order?.shipping_amount
                                        )}
                                    </div>
                                    <div className='price'>
                                        {LocalePrice(
                                            locale,
                                            orderData?.order?.discount_amount
                                        )}
                                    </div>
                                    <div className='price'>
                                        {LocalePrice(
                                            locale,
                                            orderData?.order?.base_tax_amount
                                        )}
                                    </div>

                                    <div className='price total'>
                                        {LocalePrice(
                                            locale,
                                            orderData?.order?.total_invoiced
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='order-status-form'>
                        <p>{headerText}</p>

                        <div className='inputs'>
                            <label className='input-group'>
                                <span className='label'>
                                    {t('order-status.order-id')}
                                </span>
                                <Input
                                    type='text'
                                    className={
                                        errors.orderId ? 'error' : 'input'
                                    }
                                    value={orderId}
                                    onChange={(value) =>
                                        setOrderId(value.trim())
                                    }
                                    validateInput={() => {
                                        if (orderId === '')
                                            setErrors({
                                                ...errors,
                                                orderId: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else if (!validateIsNumber(orderId))
                                            setErrors({
                                                ...errors,
                                                orderId:
                                                    t('error.numbers-only'),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                orderId: '',
                                            });
                                    }}
                                />
                                {errors.orderId && (
                                    <span className='error-message'>
                                        {errors.orderId}
                                    </span>
                                )}
                                <span className='id-info'>
                                    {t('order-status.id-info')}
                                </span>
                            </label>

                            <label className='input-group'>
                                <span className='label'>
                                    {t('order-status.email')}
                                </span>
                                <Input
                                    type='text'
                                    value={email}
                                    className={errors.email ? 'error' : 'input'}
                                    onChange={(value) => {
                                        setEmail(value.trim());
                                        if (!validateEmail(value.trim()))
                                            setErrors({
                                                ...errors,
                                                email: t('error.invalid-email'),
                                            });
                                    }}
                                    validateInput={() => {
                                        if (email === '')
                                            setErrors({
                                                ...errors,
                                                email: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else if (!validateEmail(email))
                                            setErrors({
                                                ...errors,
                                                email: t('error.invalid-email'),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                email: '',
                                            });
                                    }}
                                />
                                {errors.email && (
                                    <span className='error-message'>
                                        {errors.email}
                                    </span>
                                )}
                            </label>
                        </div>

                        {errors.notfound && <p>{errors.notfound}</p>}

                        <Button
                            label={t('order-status.get-status')}
                            disabled={
                                email === '' ||
                                orderId === '' ||
                                errors.email !== '' ||
                                errors.orderId !== '' ||
                                loading
                            }
                            onClick={() => {
                                setLoading(true);
                                fetchOrderStatus();
                            }}
                            iconRight={rightArrowIcon}
                        />
                    </div>
                )}

                {/* <div className='order-status-info'>
                    <span className='icon-container'>
                        <FontAwesomeIcon className='icon' icon={faInfo} />
                    </span>
                    <span>{content}</span>
                </div> */}
            </div>
            <div className='order-status-bottom'></div>
        </Layout>
    );
}
